import _ from "underscore";

import { camelCaseArrayWords } from "../services/converters/camel_case";
import { CLASH, DEVIATED, IN_PLACE, NOT_BUILT, PARTIALLY_BUILT } from "../models/domain/enums/scan_label";
import { INCLUDED } from "../models/domain/enums/deviation_status";

const rootStyles = window.getComputedStyle(document.documentElement);

const jsPropertyNamesToCSSPropertyNames = {
  // Colors
  almostBlackGray8: "--almost-black-gray-8",
  almostBlackGray50: "--almost-black-gray-50",
  almostBlackGray: "--almost-black-gray",
  almostWhiteGray50: "--almost-white-gray-50",
  almostWhiteGray: "--almost-white-gray",
  annotationHoveredClash: "--annotation-hovered-clash",
  annotationHoveredError: "--annotation-hovered-error",
  annotationHoveredResolved: "--annotation-hovered-dismissed",
  annotationLevelClash: "--annotation-level-clash",
  annotationLevelError: "--annotation-level-error",
  annotationLevelResolved: "--annotation-level-dismissed",
  annotationSelectedClash: "--annotation-selected-clash",
  annotationSelectedError: "--annotation-selected-error",
  annotationSelectedResolved: "--annotation-selected-dismissed",
  avvirDarkGray: "--avvir-dark-gray",
  avvirGray: "--avvir-gray",
  avvirLightGray: "--avvir-light-gray",
  avvirSecondaryBlue: "--avvir-secondary-blue",
  avvirSecondaryDarkBlue: "--avvir-secondary-dark-blue",
  avvirSecondaryLightBlue: "--avvir-secondary-light-blue",
  black: "--black",
  black5: "--black-5",
  black12: "--black-12",
  black30: "--black-30",
  black60: "--black-60",
  black80: "--black-80",
  darkGray: "--dark-gray",
  deviationErrorRed: "--deviation-error-red",
  deviationDismissedGreen: "--deviation-dismissed-green",
  disabled: "--disabled",
  extraLightGray: "--extra-light-gray",
  failurePink: "--failure-pink",
  gray: "--gray",
  insufficientDataDarkGray: "--insufficient-data-dark-gray",
  lightGray: "--light-gray",
  mediumGray: "--medium-gray",
  offWhite: "--off-white",
  secondaryContainedHoverBackground: "--avvir-hover-gray",
  selectionBlue: "--bim-selection",
  successGreen: "--success-green",
  unpushedGray: "--unpushed-gray",
  white: "--white",
  "BIM/Built": "--bim-built",
  "BIM/Not Built": "--bim-not-built",
  "BIM/Deviation": "--bim-deviation",
  "BIM/Clash": "--bim-clash",
  "BIM/Included": "--bim-included",
  "BIM/Obstructed": "--bim-obstructed",
  "BIM/Verified": "--bim-verified",
  "BIM/Partially Built": "--bim-partially-built",
  "BIM/Ignored": "--bim-ignored",
  "BIM/As Built": "--bim-as-built",
  "Status/Not Started": "--status-not-started",
  "Status/In Progress": "--status-in-progress",
  "Status/Completed": "--status-completed",
  //
  // Pangaea UI:
  //
  /* Interactive Elements */
  "Primary/Background": "--primary-background",
  "Primary/Background/Hovered": "--primary-background-hovered",
  "Primary/Background/Pressed": "--primary-background-pressed",
  "Primary/Background/Disabled": "--primary-background-disabled",
  "Secondary/Background": "--secondary-background",
  "Secondary/Background/Hovered": "--secondary-background-hovered",
  "Secondary/Background/Pressed": "--secondary-background-pressed",
  "Secondary/Background/Selected": "--secondary-background-selected",
  "Secondary/Background/Disabled": "--secondary-background-disabled",
  "Tertiary/Background": "--tertiary-background",
  "Tertiary/Background/Hovered": "--tertiary-background-hovered",
  "Tertiary/Background/Pressed": "--tertiary-background-pressed",
  "Tertiary/Background/Selected": "--tertiary-background-selected",
  "Tertiary/Background/Disabled": "--tertiary-background-disabled",
  "Primary/Text": "--primary-text",
  "Primary/Text/Hovered": "--primary-text-hovered",
  "Primary/Text/Pressed": "--primary-text-pressed",
  "Primary/Text/Disabled": "--primary-text-disabled",
  "Secondary/Text": "--secondary-text",
  "Secondary/Text/Hovered": "--secondary-text-hovered",
  "Secondary/Text/Pressed": "--secondary-text-pressed",
  "Secondary/Text/Selected": "--secondary-text-selected",
  "Secondary/Text/Disabled": "--secondary-text-disabled",
  "Tertiary/Text": "--tertiary-text",
  "Tertiary/Text/Hovered": "--tertiary-text-hovered",
  "Tertiary/Text/Pressed": "--tertiary-text-pressed",
  "Tertiary/Text/Selected": "--tertiary-text-selected",
  "Tertiary/Text/Disabled": "--tertiary-text-disabled",
  /* Backgrounds: */
  "Background/Base": "--base-background",
  "Background/Side Panel": "--side-panel-background",
  "Background/Navigation/First Level": "--first-level-navigation-background",
  "Background/Navigation/Second Level": "--second-level-navigation-background",
  "Background/Divider": "--divider-color",
  "Background/Backdrop": "--backdrop-color",
  "Background/Dialog": "--dialog-background",
  /* Alerts: */
  /* Error: */
  "Error/50": "--red-50",
  "Error/100": "--red-100",
  "Error/200": "--red-200",
  "Error/300": "--red-300",
  "Error/400": "--red-400",
  "Error/500": "--red-500",
  "Error/600": "--red-600",
  "Error/700": "--red-700",
  "Error/800": "--red-800",
  "Error/900": "--red-900",
  /* Warning: */
  "Warning/50": "--orange-50",
  "Warning/100": "--orange-100",
  "Warning/200": "--orange-200",
  "Warning/300": "--orange-300",
  "Warning/400": "--orange-400",
  "Warning/500": "--orange-500",
  "Warning/600": "--orange-600",
  "Warning/700": "--orange-700",
  "Warning/800": "--orange-800",
  "Warning/900": "--orange-900",
  /* Success: */
  "Success/50": "--green-50",
  "Success/100": "--green-100",
  "Success/200": "--green-200",
  "Success/300": "--green-300",
  "Success/400": "--green-400",
  "Success/500": "--green-500",
  "Success/600": "--green-600",
  "Success/700": "--green-700",
  "Success/800": "--green-800",
  "Success/900": "--green-900",
  /* Info: */
  "Info/50": "--blue-50",
  "Info/100": "--blue-100",
  "Info/200": "--blue-200",
  "Info/300": "--blue-300",
  "Info/400": "--blue-400",
  "Info/500": "--blue-500",
  "Info/600": "--blue-600",
  "Info/700": "--blue-700",
  "Info/800": "--blue-800",
  "Info/900": "--blue-900",

  // Sizes
  captionFontSize: "--caption-font",
  captionLineHeight: "--caption-line-height",
  bodyFontSize: "--body-font",
  bodyLineHeight: "--body-line-height",
  bodyLargeFontSize: "--body-large-font",
  bodyLargeLineHeight: "--body-large-line-height",
  subtitleFontSize: "--subtitle-font",
  subtitleLineHeight: "--subtitle-line-height",
  titleFontSize: "--title-font",
  titleLineHeight: "--title-line-height",
  titleLargeFontSize: "--title-large-font",
  titleLargeLineHeight: "--title-large-line-height",
  smallestFontSize: "--smallest-font",
  smallFontSize: "--small-font",
  smallMediumFontSize: "--small-medium-font",
  mediumFontSize: "--medium-font",
  mediumLargeFontSize: "--medium-large-font",
  largeFontSize: "--large-font",
  defaultIconSize: "--default-icon-size",
  largeIconSize: "--large-icon-size",
  halfSpace: "--half-space",
  space: "--space",
  space2x: "--space-x2",
  space3x: "--space-x3",
  space4x: "--space-x4",
  padding: "--padding",
  navBarHeight: "--nav-bar-height",
  filterBarHeight: "--filter-bar-height",
};

export type GlobalStyles = {
  almostBlackGray8: string
  almostBlackGray50: string
  almostBlackGray: string
  almostWhiteGray50: string
  almostWhiteGray: string
  annotationHoveredClash: string
  annotationHoveredError: string
  annotationHoveredResolved: string
  annotationLevelClash: string
  annotationLevelError: string
  annotationLevelResolved: string
  annotationSelectedClash: string
  annotationSelectedError: string
  annotationSelectedResolved: string
  avvirDarkGray: string
  avvirGray: string
  avvirLightGray: string
  avvirSecondaryBlue: string
  avvirSecondaryDarkBlue: string
  avvirSecondaryLightBlue: string
  black: string
  black5: string
  black12: string
  black30: string
  black60: string
  black80: string
  darkGray: string
  deviationDismissedGreen: string
  deviationErrorRed: string
  disabled: string
  extraLightGray: string
  failurePink: string
  gray: string
  insufficientDataDarkGray: string
  lightGray: string
  mediumGray: string
  offWhite: string
  secondaryContainedHoverBackground: string
  selectionBlue: string
  successGreen: string
  unpushedGray: string
  white: string
  "BIM/Built": string
  "BIM/Not Built": string
  "BIM/Deviation": string
  "BIM/Clash": string
  "BIM/Included": string
  "BIM/Obstructed": string
  "BIM/Verified": string
  "BIM/Ignored": string
  "BIM/Partially Built": string
  "BIM/As Built": string
  "Status/Not Started": string
  "Status/In Progress": string
  "Status/Completed": string
  "Primary/Background": string
  "Primary/Background/Hovered": string
  "Primary/Background/Pressed": string
  "Primary/Background/Disabled": string
  "Secondary/Background/Hovered": string
  "Secondary/Background/Pressed": string
  "Secondary/Background/Selected": string
  "Secondary/Background/Disabled": string
  "Tertiary/Background/Hovered": string
  "Tertiary/Background/Pressed": string
  "Tertiary/Background/Selected": string
  "Tertiary/Background/Disabled": string
  "Primary/Text": string
  "Primary/Text/Hovered": string
  "Primary/Text/Pressed": string
  "Primary/Text/Disabled": string
  "Secondary/Text": string
  "Secondary/Text/Hovered": string
  "Secondary/Text/Pressed": string
  "Secondary/Text/Selected": string
  "Secondary/Text/Disabled": string
  "Tertiary/Text": string
  "Tertiary/Text/Hovered": string
  "Tertiary/Text/Pressed": string
  "Tertiary/Text/Selected": string
  "Tertiary/Text/Disabled": string
  "Background/Base": string,
  "Background/Side Panel": string,
  "Background/Navigation/First Level": string,
  "Background/Navigation/Second Level": string,
  "Background/Divider": string,
  "Background/Backdrop": string,
  "Background/Dialog": string,
  "Error/50": string,
  "Error/100": string,
  "Error/200": string,
  "Error/300": string,
  "Error/400": string,
  "Error/500": string,
  "Error/600": string,
  "Error/700": string,
  "Error/800": string,
  "Error/900": string,
  "Warning/50": string,
  "Warning/100": string,
  "Warning/200": string,
  "Warning/300": string,
  "Warning/400": string,
  "Warning/500": string,
  "Warning/600": string,
  "Warning/700": string,
  "Warning/800": string,
  "Warning/900": string,
  "Success/50": string,
  "Success/100": string,
  "Success/200": string,
  "Success/300": string,
  "Success/400": string,
  "Success/500": string,
  "Success/600": string,
  "Success/700": string,
  "Success/800": string,
  "Success/900": string,
  "Info/50": string,
  "Info/100": string,
  "Info/200": string,
  "Info/300": string,
  "Info/400": string,
  "Info/500": string,
  "Info/600": string,
  "Info/700": string,
  "Info/800": string,
  "Info/900": string,

  captionFontSize: number
  captionLineHeight: number
  bodyFontSize: number
  bodyLineHeight: number
  bodyLargeFontSize: number
  bodyLargeLineHeight: number
  subtitleFontSize: number
  subtitleLineHeight: number
  titleFontSize: number
  titleLineHeight: number
  titleLargeFontSize: number
  titleLargeLineHeight: number
  smallestFontSize: number
  smallFontSize: number
  smallMediumFontSize: number
  mediumFontSize: number
  mediumLargeFontSize: number
  largeFontSize: number
  defaultIconSize: number
  largeIconSize: number
  halfSpace: number
  space: number
  space2x: number
  space3x: number
  space4x: number
  padding: number
  navBarHeight: number
  filterBarHeight: number
}

type CssVariables = GlobalStyles

function getCssVariablesAsObject(): CssVariables {
  const styleBlocks = Array.from(document.querySelectorAll("style"));
  const computedProperties: GlobalStyles = _.mapObject(jsPropertyNamesToCSSPropertyNames, (cssPropertyName) => {
      let value: number | string = rootStyles.getPropertyValue(cssPropertyName).trim();
      if (value.endsWith("px")) {
        value = parseFloat(value.slice(0, -2));
      }
      if (value === "" &&
          (cssPropertyName.includes("space") || cssPropertyName.includes("padding"))
      ) {
        value = Math.round(Math.random() * 100);
      }
      if (value === "") {
        value = _.sample(["#0fa", "#a0f", "#f02", "#ff2", "#ff0", "#0ff", "#444", "#9fa", "#cc0", "#cc4"]);
      }
      return value;
    }
  ) as GlobalStyles;

  return styleBlocks.reduce((cssVariablesMap, block) => {
    const blockCssVariables = block.innerHTML.match(/--.+?:.+?;/g);
    if (blockCssVariables && blockCssVariables.length) {
      blockCssVariables.forEach((variable) => {
        if (typeof variable === "string") {
          const keyVal = variable.split(":");
          const key = keyVal[0].slice(2);
          const camelCaseVariableName = key.split("-").map(camelCaseArrayWords).join("");
          if (!cssVariablesMap[camelCaseVariableName]) {
            let value: number | string = keyVal[1].slice(1, -1);
            if (value.endsWith("px")) {
              value = parseFloat(value.slice(0, -2));
            }
            cssVariablesMap[camelCaseVariableName] = value;
          }
        }
      });
    }
    return cssVariablesMap;
  }, computedProperties);
}

const globalStyles: GlobalStyles = getCssVariablesAsObject();

export const statusColorsByStatus = {
  [CLASH]: "BIM/Clash" as const,
  [IN_PLACE]: "BIM/Built" as const,
  [NOT_BUILT]: "BIM/Not Built" as const,
  [DEVIATED]: "BIM/Deviation" as const,
  [INCLUDED]: "BIM/Included" as const,
  [PARTIALLY_BUILT]: "BIM/Partially Built" as const,
  VERIFIED: "BIM/Verified" as const,
  EXCLUDE_FROM_ANALYSIS: "BIM/Ignored" as const,
};

export default globalStyles;
